/**
 * ComposeModal — create or edit a scheduled / send-now message (M1.14 wave 3).
 *
 * Edit-mode locks the channel — the user has to delete and recreate to move
 * to a different channel. This is intentional: a scheduled message's
 * recipient shape (Telegram chat vs email To/Cc/Bcc vs Facebook page) is
 * channel-specific, and remapping in place would create a state-machine
 * tangle that's not worth the carry.
 *
 * Email gets the extended set (subject + recipient lists + attachments); the
 * non-email channels share the simpler "body + when" form.
 *
 * "Send now" persists with `sendNow: true` — the message will fire as soon as
 * channel connectors land. Until then it's a saved draft that shows on the
 * calendar with an "(immediate)" time stamp.
 */
import type {
  CommunicationAttachmentStaged,
  CommunicationChannelView,
  CommunicationRecipientListView,
  CommunicationScheduledMessageView,
  CreateCommunicationScheduledMessageRequest,
} from '@peerless/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Loader2,
  Send,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useConfirm } from '../../components/ConfirmProvider';
import { ApiError, apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';

import { AttachmentsField } from './AttachmentsField';
import { fromLocalInputValue, toLocalInputValue, withTenAM } from './date-helpers';
import { EmailRecipientsField } from './EmailRecipientsField';
import { KIND_META } from './kind-meta';


type ComposeMode = 'now' | 'time';

export function ComposeModal({
  channels,
  editing,
  defaultDate,
  onClose,
}: {
  channels: CommunicationChannelView[];
  editing: CommunicationScheduledMessageView | null;
  defaultDate: Date | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const isEdit = !!editing;

  const initialChannelId = useMemo(() => {
    if (editing) return editing.channelId;
    return channels[0]?.id ?? '';
  }, [editing, channels]);

  const [channelId, setChannelId] = useState(initialChannelId);
  const channel = channels.find((c) => c.id === channelId) ?? null;
  const isEmail = channel?.kind === 'email_imap';

  const [body, setBody] = useState(editing?.body ?? '');
  const [emailSubject, setEmailSubject] = useState(editing?.emailSubject ?? '');
  const [emailTo, setEmailTo] = useState<string[]>(editing?.emailTo ?? []);
  const [emailCc, setEmailCc] = useState<string[]>(editing?.emailCc ?? []);
  const [emailBcc, setEmailBcc] = useState<string[]>(editing?.emailBcc ?? []);
  const [attachments, setAttachments] = useState<CommunicationAttachmentStaged[]>(
    editing?.attachments ?? [],
  );
  const [mode, setMode] = useState<ComposeMode>(() => {
    if (editing?.sendNow) return 'now';
    if (editing?.scheduledAt) return 'time';
    // Default to scheduled when launched from a calendar cell, otherwise
    // 'now'. The previous `defaultDate ? 'time' : 'time'` always resolved to
    // 'time' regardless of the source.
    return defaultDate ? 'time' : 'now';
  });
  const [whenLocal, setWhenLocal] = useState<string>(() => {
    const seed = editing?.scheduledAt
      ? new Date(editing.scheduledAt)
      : defaultDate
        ? withTenAM(defaultDate)
        : null;
    return seed ? toLocalInputValue(seed) : '';
  });

  const recipientListsQuery = useQuery({
    queryKey: queryKeys.communication.recipientLists(),
    queryFn: () =>
      apiRequest<CommunicationRecipientListView[]>('/communication/recipient-lists'),
    enabled: isEmail,
  });

  const confirm = useConfirm();
  const createMutation = useMutation({
    mutationFn: (req: CreateCommunicationScheduledMessageRequest) =>
      apiRequest<CommunicationScheduledMessageView>(
        '/communication/scheduled-messages',
        { method: 'POST', body: req },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.communication.all() });
      toast.success(mode === 'now' ? 'Message saved (will fire immediately once connectors land)' : 'Scheduled');
      onClose();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });

  const updateMutation = useMutation({
    mutationFn: (req: Partial<CreateCommunicationScheduledMessageRequest>) =>
      apiRequest<CommunicationScheduledMessageView>(
        `/communication/scheduled-messages/${editing!.id}`,
        { method: 'PATCH', body: req },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.communication.all() });
      toast.success('Updated');
      onClose();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiRequest(`/communication/scheduled-messages/${editing!.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.communication.all() });
      toast.success('Deleted');
      onClose();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  const submit = (): void => {
    if (!channelId) {
      toast.error('Pick a channel');
      return;
    }
    if (!body.trim()) {
      toast.error('Body is required');
      return;
    }
    if (isEmail && emailTo.length === 0) {
      toast.error('Add at least one recipient');
      return;
    }
    if (mode === 'time' && !whenLocal) {
      toast.error('Pick a date/time');
      return;
    }
    const req: CreateCommunicationScheduledMessageRequest = {
      channelId,
      body,
    };
    if (isEmail) {
      req.emailTo = emailTo;
      if (emailCc.length > 0) req.emailCc = emailCc;
      if (emailBcc.length > 0) req.emailBcc = emailBcc;
      if (emailSubject.trim()) req.emailSubject = emailSubject.trim();
      if (attachments.length > 0) req.attachments = attachments;
    }
    if (mode === 'now') {
      req.sendNow = true;
    } else {
      req.scheduledAt = fromLocalInputValue(whenLocal);
    }
    if (isEdit) updateMutation.mutate(req);
    else createMutation.mutate(req);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-start justify-center bg-bg/80 px-4 pt-[5vh] backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded border border-border bg-surface shadow-2xl">
        <header className="flex items-center justify-between border-b border-border bg-bg/30 px-5 py-3">
          <h3 className="text-sm font-semibold text-text">
            {isEdit ? 'Edit scheduled message' : 'Compose'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex size-7 items-center justify-center rounded-sm text-muted hover:bg-bg hover:text-text"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="space-y-4 p-5">
          {/* Channel picker */}
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-tight text-muted">
              Channel
            </p>
            <div className="flex flex-wrap gap-2">
              {channels.map((c) => {
                const meta = KIND_META[c.kind];
                const selected = channelId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={isEdit}
                    onClick={() => setChannelId(c.id)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1 text-xs transition disabled:opacity-50',
                      selected
                        ? 'border-accent bg-accent/10 text-text'
                        : 'border-border bg-bg/30 text-muted hover:bg-bg hover:text-text',
                    )}
                  >
                    <meta.Icon className="size-3.5" />
                    {c.label}
                  </button>
                );
              })}
            </div>
            {isEdit && (
              <p className="mt-1 text-[10px] text-muted">
                Channel is locked when editing — delete + recreate to move to another channel.
              </p>
            )}
          </div>

          {/* Email-only header fields */}
          {isEmail && (
            <>
              <EmailRecipientsField
                label="To"
                values={emailTo}
                setValues={setEmailTo}
                lists={recipientListsQuery.data ?? []}
              />
              <EmailRecipientsField
                label="Cc"
                values={emailCc}
                setValues={setEmailCc}
                lists={recipientListsQuery.data ?? []}
              />
              <EmailRecipientsField
                label="Bcc"
                values={emailBcc}
                setValues={setEmailBcc}
                lists={recipientListsQuery.data ?? []}
              />
              <Field
                label="Subject"
                value={emailSubject}
                onChange={setEmailSubject}
                placeholder="Subject…"
              />
            </>
          )}

          {/* Body */}
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-tight text-muted">
              {isEmail ? 'Body' : 'Message'}
            </p>
            <textarea
              rows={isEmail ? 8 : 5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={
                isEmail
                  ? 'Write your email body…'
                  : 'Write your message to the group / channel…'
              }
              className="block w-full rounded-sm border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted focus:border-accent focus:outline-none"
            />
          </div>

          {/* Attachments (email only) */}
          {isEmail && (
            <AttachmentsField values={attachments} setValues={setAttachments} />
          )}

          {/* When */}
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-tight text-muted">
              When
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setMode('now')}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-xs transition',
                  mode === 'now'
                    ? 'border-accent bg-accent/10 text-text'
                    : 'border-border bg-bg/30 text-muted hover:bg-bg hover:text-text',
                )}
              >
                <Send className="size-3" />
                Send now
              </button>
              <button
                type="button"
                onClick={() => setMode('time')}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-xs transition',
                  mode === 'time'
                    ? 'border-accent bg-accent/10 text-text'
                    : 'border-border bg-bg/30 text-muted hover:bg-bg hover:text-text',
                )}
              >
                <CalendarClock className="size-3" />
                Schedule
              </button>
              {mode === 'time' && (
                <input
                  type="datetime-local"
                  value={whenLocal}
                  onChange={(e) => setWhenLocal(e.target.value)}
                  className="rounded-sm border border-border bg-bg px-2 py-1 text-xs text-text focus:border-accent focus:outline-none"
                />
              )}
            </div>
            <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted">
              <ShieldAlert className="size-3" />
              Channel connectors are not wired yet — messages are persisted and shown on
              the calendar, but actual dispatch lands in a later phase.
            </p>
          </div>
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-border bg-bg/30 px-5 py-3">
          <div>
            {isEdit && (
              <button
                type="button"
                onClick={async () => {
                  if (await confirm({ title: 'Delete this scheduled message?', destructive: true })) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-surface px-3 py-1.5 text-sm text-danger hover:bg-danger/10 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Trash2 className="size-3.5" />
                )}
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm border border-border bg-surface px-3 py-1.5 text-sm text-muted hover:bg-bg hover:text-text"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-sm bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:opacity-90 disabled:opacity-50"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="size-3.5 animate-spin" />
              )}
              {mode === 'now' ? (isEdit ? 'Save' : 'Send now') : (isEdit ? 'Save' : 'Schedule')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-tight text-muted">
        {label}
      </p>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="block w-full rounded-sm border border-border bg-bg px-3 py-1.5 text-sm text-text placeholder:text-muted focus:border-accent focus:outline-none"
      />
    </div>
  );
}
